import React from 'react'

interface RushmindInsightTextProps {
  text: string
  className?: string
  processMarkdown?: boolean
}

const RushmindInsightText: React.FC<RushmindInsightTextProps> = ({
  text,
  className = '',
  processMarkdown = false,
}) => {
  // O texto do insight vem do LLM e pode citar, verbatim, nomes de projeto/cliente/pessoa
  // cadastrados por qualquer usuário do sistema (dado não confiável). Como isso é injetado via
  // dangerouslySetInnerHTML mais abaixo, escapamos entidades HTML ANTES de aplicar as
  // transformações de markdown, pra um nome malicioso (ex: um projeto chamado
  // "<img src=x onerror=alert(1)>") virar texto inerte em vez de HTML executável.
  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  // Só vira link de verdade se a URL for um caminho interno relativo (ex: /projects/123).
  // Bloqueia esquemas perigosos (javascript:, data:) e links "protocol-relative" (//host).
  const isSafeInternalPath = (url: string): boolean => /^\/(?!\/)[^\s"'<>]*$/.test(url)

  // Função para processar Markdown e converter links em HTML
  const processMarkdownLinks = (text: string): string => {
    let processed = escapeHtml(text)

    // Processa LINKS PRIMEIRO (antes de bold)
    // Links com texto em negrito: [**texto**](url)
    processed = processed.replace(/\[\*\*(.+?)\*\*\]\((.+?)\)/g, (_m, p1, p2) =>
      isSafeInternalPath(p2) ? `<a href="${p2}" class="rushmind-link"><strong>${p1}</strong></a>` : `<strong>${p1}</strong>`
    )
    // Links normais: [texto](url). Usa .+? (não [^\]]+?) porque o texto do link pode ser um
    // nome que já contém colchetes (ex: projeto "[DLOA] Nome"); o regex para no primeiro "](" encontrado.
    processed = processed.replace(/\[(.+?)\]\((.+?)\)/g, (_m, p1, p2) =>
      isSafeInternalPath(p2) ? `<a href="${p2}" class="rushmind-link">${p1}</a>` : p1
    )

    // Converte **texto** para <strong>texto</strong> (DEPOIS dos links)
    processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

    return processed
  }

  // Função para destacar números e nomes no texto.
  //
  // IMPORTANTE: essa função roda DEPOIS de processMarkdownLinks, então o texto já pode conter
  // tags <a href="/projects/631" class="rushmind-link">...</a> montadas. A regex de números não
  // sabe distinguir "631 tarefas" (texto de verdade) de "631" dentro de href="/projects/631"
  // (um atributo HTML) — se rodar sem essa proteção, ela injeta um <strong> DENTRO do valor do
  // atributo href, o navegador fecha o atributo na primeira aspas que encontra e o resto do link
  // (class="rushmind-link">Nome do Projeto) vaza como texto visível na tela (bug real visto em
  // produção). Por isso: nunca tocar no conteúdo de uma tag <a>...</a> já montada, só no que
  // está fora dela.
  //
  // Mesmo problema com entidades HTML numéricas: escapeHtml converte ' em &#39;, e "39" bate na
  // numberRegex. Sem essa proteção, &#39; virava &#<strong...>39</strong>;, deixando de ser uma
  // entidade válida — o navegador não decodifica mais nada e mostra "&#39;" literal na tela (bug
  // real visto em produção, ex.: nome de projeto entre aspas simples no texto do insight).
  const highlightText = (text: string): string => {
    const numberRegex = /(\d+(?:[.,]\d+)?(?:%|h|R\$)?|\d+(?:[.,]\d+)?\s*(?:dias?|semanas?|meses?|anos?|horas?|minutos?|projetos?|tarefas?|usuários?|clientes?))/gi

    const highlightSegment = (segment: string) =>
      segment.replace(numberRegex, (match) => {
        return `<strong class="number-highlight">${match}</strong>`
      })

    // Case-insensitive e [\s\S] (não .) pra não depender de a tag <a> ser sempre minúscula,
    // com espaço logo após, e numa linha só — mesmo sem isso ser hoje um caminho de exploração
    // real (só quem gera <a> aqui é o processMarkdownLinks acima, sempre nesse formato exato),
    // não custa não depender dessa suposição se o gerador mudar no futuro.
    return text
      .split(/(<a\b[^>]*>[\s\S]*?<\/a>|&#\d+;)/gi)
      .map((part) => (/^<a\b/i.test(part) || /^&#\d+;$/.test(part) ? part : highlightSegment(part)))
      .join('')
  }

  // escapeHtml roda sempre, mesmo com processMarkdown=false: o span abaixo usa
  // dangerouslySetInnerHTML incondicionalmente, então nunca pode receber texto não-escapado,
  // independente de quem chamar o componente pedir o processamento de link/negrito ou não.
  const processedFullText = processMarkdown ? processMarkdownLinks(text) : escapeHtml(text)
  const highlightedFullText = highlightText(processedFullText)

  return <span className={className} dangerouslySetInnerHTML={{__html: highlightedFullText}} />
}

export default RushmindInsightText
